// M32 — Feedback widget data layer.
//
// Two responsibilities:
//   1. Collect runtime metadata that helps the maintainer reproduce
//      whatever the user is reporting (page URL, viewport, mobile
//      device flags, app version, …).
//   2. Persist a feedback submission: insert the row + optionally
//      upload a screenshot to the `feedback-screenshots` bucket.
//
// Everything mobile-aware is in `collectMetadata()`. The dialog passes
// the metadata back into `submitFeedback()` so the "What we'll send"
// preview matches the row that's written. Single source of truth.

import { supabase } from './supabase';
import type { FeedbackKind, FeedbackMetadata } from './types';

// Module-level constants — kept here so the dialog can quote the
// screenshot size hint to the user without re-deriving it.
export const FEEDBACK_MESSAGE_MAX = 5000;
export const FEEDBACK_SCREENSHOT_BUCKET = 'feedback-screenshots';
/** Cap on uploaded screenshot bytes. html2canvas output is JPEG-encoded
 * downstream to stay under this; bigger images get rejected before the
 * upload call. ~1 MB is plenty for the viewport on retina mobile. */
export const FEEDBACK_SCREENSHOT_MAX_BYTES = 1_500_000;

/**
 * Sniff every metadata field. Every read is guarded — feature-detection
 * matters here because:
 *   - Older Safari doesn't expose `screen.orientation`
 *   - Firefox doesn't expose `navigator.connection`
 *   - Privacy-hardened browsers (Brave, hardened Safari) return `null`
 *     for `userAgentData`
 *   - Server-side execution (vitest setupFiles, SSR) has no `window`
 *
 * Anything that throws or is missing becomes `null`. The row schema
 * allows nulls everywhere except `kind` + `message`.
 */
export function collectMetadata(): FeedbackMetadata {
  // SSR / non-browser safety. The hook is always called inside a React
  // tree so this branch is mostly belt-and-suspenders, but vitest with
  // happy-dom does NOT define every navigator field — guarding keeps
  // unit tests honest.
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return emptyMetadata();
  }

  // ────── Optional / vendor-specific globals (typed locally so the
  // ────── module surface stays clean) ────────────────────────────
  interface NetworkInformation {
    effectiveType?: string;
  }
  interface NavigatorUAData {
    platform?: string;
  }
  interface NavigatorWithExtras extends Navigator {
    connection?: NetworkInformation;
    deviceMemory?: number;
    userAgentData?: NavigatorUAData;
  }
  const nav = navigator as NavigatorWithExtras;

  return {
    page_url: safe(() => window.location.href),
    app_version: safe(() =>
      typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : null,
    ),

    user_agent: safe(() => navigator.userAgent),
    locale: safe(() => navigator.language),
    timezone: safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone),

    viewport_width: safe(() => window.innerWidth),
    viewport_height: safe(() => window.innerHeight),
    screen_width: safe(() => window.screen?.width ?? null),
    screen_height: safe(() => window.screen?.height ?? null),
    device_pixel_ratio: safe(() => window.devicePixelRatio ?? null),

    is_touch_device: safe(
      () => 'ontouchstart' in window || (navigator.maxTouchPoints ?? 0) > 0,
    ),
    orientation: safe(() => window.screen?.orientation?.type ?? null),
    // userAgentData is the modern, privacy-respecting source; navigator.platform
    // is the legacy fallback. Both return null in browsers that hide them.
    platform: safe(
      () => nav.userAgentData?.platform ?? navigator.platform ?? null,
    ),
    is_standalone: safe(
      () => window.matchMedia('(display-mode: standalone)').matches,
    ),
    connection_type: safe(() => nav.connection?.effectiveType ?? null),
    device_memory: safe(() => nav.deviceMemory ?? null),
  };
}

/**
 * Run a metadata sniff, swallow any throw, and downgrade undefined to
 * null. Keeps every call site one line and prevents one bad field from
 * tanking the whole submission.
 */
function safe<T>(fn: () => T): T | null {
  try {
    const v = fn();
    return v === undefined ? null : v;
  } catch {
    return null;
  }
}

function emptyMetadata(): FeedbackMetadata {
  return {
    page_url: null,
    app_version: null,
    user_agent: null,
    locale: null,
    timezone: null,
    viewport_width: null,
    viewport_height: null,
    screen_width: null,
    screen_height: null,
    device_pixel_ratio: null,
    is_touch_device: null,
    orientation: null,
    platform: null,
    is_standalone: null,
    connection_type: null,
    device_memory: null,
  };
}

export interface SubmitFeedbackInput {
  kind: FeedbackKind;
  message: string;
  metadata: FeedbackMetadata;
  /** Optional error context (name + message + truncated stack) when the
   * dialog was launched from the Sentry ErrorBoundary. */
  errorContext?: string | null;
  /** Optional PNG/JPEG blob to upload. Caller has already capped size +
   * downscaled. */
  screenshot?: Blob | null;
}

export interface SubmitFeedbackResult {
  id: string;
  screenshotPath: string | null;
}

/**
 * Insert a feedback row and (optionally) upload its screenshot.
 *
 * Ordering: upload first, row second. If the row insert fails after a
 * successful upload, the orphan object is removed before bailing.
 * Same cleanup-on-failure pattern as `uploadDocument()`.
 */
export async function submitFeedback(
  input: SubmitFeedbackInput,
): Promise<SubmitFeedbackResult> {
  const message = input.message.trim();
  if (!message) {
    throw new Error('Message is required');
  }
  if (message.length > FEEDBACK_MESSAGE_MAX) {
    throw new Error(`Message is too long (max ${FEEDBACK_MESSAGE_MAX} characters)`);
  }

  // Identify the inserting user — may be null if submitting from a
  // public route (e.g. the login page if it ever wires the widget in).
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id ?? null;
  const feedbackId = crypto.randomUUID();

  // ───── Upload screenshot first (if any) ────────────────────────
  let screenshotPath: string | null = null;
  if (input.screenshot) {
    if (input.screenshot.size > FEEDBACK_SCREENSHOT_MAX_BYTES) {
      throw new Error(
        `Screenshot is too large (${Math.round(input.screenshot.size / 1024)} KB; max ${Math.round(FEEDBACK_SCREENSHOT_MAX_BYTES / 1024)} KB)`,
      );
    }
    const folder = userId ?? 'anon';
    const ext = blobExtension(input.screenshot);
    const path = `${folder}/${feedbackId}.${ext}`;
    const { error: upErr } = await supabase.storage
      .from(FEEDBACK_SCREENSHOT_BUCKET)
      .upload(path, input.screenshot, {
        cacheControl: '3600',
        upsert: false,
        contentType: input.screenshot.type || 'image/jpeg',
      });
    if (upErr) {
      throw new Error(`Screenshot upload failed: ${upErr.message}`);
    }
    screenshotPath = path;
  }

  // ───── Insert the feedback row ─────────────────────────────────
  const { error: insErr } = await supabase.from('feedback').insert({
    id: feedbackId,
    user_id: userId,
    kind: input.kind,
    message,
    screenshot_path: screenshotPath,
    error_context: input.errorContext ?? null,
    ...input.metadata,
  });

  if (insErr) {
    // Clean up the orphan screenshot so it doesn't sit in storage with
    // no row pointing at it.
    if (screenshotPath) {
      await supabase.storage
        .from(FEEDBACK_SCREENSHOT_BUCKET)
        .remove([screenshotPath]);
    }
    throw new Error(`Feedback submit failed: ${insErr.message}`);
  }

  return { id: feedbackId, screenshotPath };
}

function blobExtension(blob: Blob): string {
  if (blob.type === 'image/png') return 'png';
  if (blob.type === 'image/webp') return 'webp';
  return 'jpg';
}

/**
 * Format a thrown value into the `error_context` string we store on a
 * feedback row when the dialog is launched from the Sentry
 * ErrorBoundary. Stack is truncated to keep the row small (DB CHECK
 * caps at 8000 chars).
 */
export function formatErrorContext(err: unknown): string {
  if (!err) return '';
  if (err instanceof Error) {
    const stack = err.stack ?? '';
    const truncated = stack.length > 6000 ? stack.slice(0, 6000) + '\n…(truncated)' : stack;
    return `${err.name}: ${err.message}\n\n${truncated}`.trim();
  }
  try {
    return String(err).slice(0, 6000);
  } catch {
    return '(unable to serialize error)';
  }
}
